import { computed, reactive, readonly } from 'vue';
import { apiRequest } from '@/lib/client-api';

const TOKEN_KEY = 'sweet_home_client_token';

const state = reactive({
  token: typeof window !== 'undefined' ? window.localStorage.getItem(TOKEN_KEY) || '' : '',
  user: null,
  initialized: false,
});

function persistToken(token) {
  state.token = token || '';

  if (typeof window === 'undefined') {
    return;
  }

  if (token) {
    window.localStorage.setItem(TOKEN_KEY, token);
  } else {
    window.localStorage.removeItem(TOKEN_KEY);
  }
}

function clearSession() {
  persistToken('');
  state.user = null;
  state.initialized = true;
}

async function initializeSession(force = false) {
  if (state.initialized && !force) {
    return state.user;
  }

  if (!state.token) {
    state.user = null;
    state.initialized = true;
    return null;
  }

  try {
    const response = await apiRequest('/auth/me', { token: state.token });
    state.user = response.data;
  } catch (error) {
    clearSession();
    throw error;
  }

  state.initialized = true;
  return state.user;
}

async function login(payload) {
  const response = await apiRequest('/auth/login', {
    method: 'POST',
    data: { ...payload, device_name: 'ecom-web' },
  });

  persistToken(response.meta.token);
  state.user = response.data;
  state.initialized = true;

  return response.data;
}

async function register(payload) {
  const response = await apiRequest('/auth/register', {
    method: 'POST',
    data: { ...payload, device_name: 'ecom-web' },
  });

  persistToken(response.meta.token);
  state.user = response.data;
  state.initialized = true;

  return response.data;
}

async function logout() {
  try {
    if (state.token) {
      await apiRequest('/auth/logout', {
        method: 'POST',
        token: state.token,
      });
    }
  } finally {
    clearSession();
  }
}

async function refreshProfile() {
  const response = await apiRequest('/profile', {
    token: state.token,
  });

  state.user = {
    ...state.user,
    ...response.data,
  };

  return state.user;
}

async function updateProfile(payload) {
  const response = await apiRequest('/profile', {
    method: 'PATCH',
    token: state.token,
    data: payload,
  });

  state.user = {
    ...state.user,
    ...response.data,
  };

  return state.user;
}

async function updatePassword(payload) {
  const response = await apiRequest('/profile/password', {
    method: 'PATCH',
    token: state.token,
    data: payload,
  });

  return response;
}

export function useClientSession() {
  return {
    state: readonly(state),
    isAuthenticated: computed(() => Boolean(state.token && state.user)),
    initializeSession,
    login,
    register,
    logout,
    refreshProfile,
    updateProfile,
    updatePassword,
    token: computed(() => state.token),
  };
}
